import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useAdminContent = (slug, password, navigate) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [template, setTemplate] = useState(null);
  const [content, setContent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tmplRes, contentRes] = await Promise.all([
        axios.get(`${API}/templates/${slug}`),
        axios.get(`${API}/content/${slug}`)
      ]);
      setTemplate(tmplRes.data);
      setContent(contentRes.data);
    } catch {
      try {
        const tmplRes = await axios.get(`${API}/templates/${slug}`);
        setTemplate(tmplRes.data);
        setContent({
          seo_title: '', meta_description: '', h1: '',
          origin_story: '', how_to_use: '',
          fun_facts: [], example_captions: [], related_slugs: [], faq: []
        });
      } catch {
        toast.error('Template not found');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const score = useMemo(() => {
    if (!content) return 0;
    let s = 0;
    if ((content.origin_story || '').length >= 100) s += 25;
    if ((content.example_captions || []).length >= 10) s += 20;
    if ((content.how_to_use || '').length >= 50) s += 15;
    if ((content.fun_facts || []).length >= 3) s += 15;
    if ((content.related_slugs || []).length >= 3) s += 10;
    if (content.seo_title && content.meta_description) s += 15;
    return s;
  }, [content]);

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${slug}?password=${password}`, content);
      toast.success('Content saved');
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/admin/generate-content/${slug}?password=${password}`);
      setContent(res.data.content);
      toast.success('AI content regenerated');
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRegenerating(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await axios.put(`${API}/admin/content/${slug}?password=${password}`, content);
      await axios.post(`${API}/admin/publish/${slug}?password=${password}`);
      toast.success('Published!');
      setTemplate(prev => ({ ...prev, pipeline_status: 'PUBLISHED' }));
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      await axios.post(`${API}/admin/pipeline/${slug}/status?password=${password}`, {
        new_status: 'READY'
      });
      setTemplate(prev => ({ ...prev, pipeline_status: 'READY' }));
      toast.success('Unpublished — status set to READY');
    } catch (error) {
      toast.error('Failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPublishing(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.post(`${API}/admin/pipeline/${slug}/status?password=${password}`, {
        new_status: newStatus
      });
      setTemplate(prev => ({ ...prev, pipeline_status: newStatus }));
      toast.success(`Status updated to ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  // List helpers
  const addListItem = (field) => {
    setContent(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  };
  const updateListItem = (field, index, value) => {
    setContent(prev => {
      const arr = [...(prev[field] || [])];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };
  const removeListItem = (field, index) => {
    setContent(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  // FAQ helpers
  const addFaq = () => {
    setContent(prev => ({
      ...prev,
      faq: [...(prev.faq || []), { question: '', answer: '' }]
    }));
  };
  const updateFaq = (index, field, value) => {
    setContent(prev => {
      const faq = [...(prev.faq || [])];
      faq[index] = { ...faq[index], [field]: value };
      return { ...prev, faq };
    });
  };
  const removeFaq = (index) => {
    setContent(prev => ({
      ...prev,
      faq: (prev.faq || []).filter((_, i) => i !== index)
    }));
  };

  return {
    loading, saving, regenerating, publishing,
    template, content, score,
    updateField, handleSave, handleRegenerate,
    handlePublish, handleUnpublish, handleStatusChange,
    addListItem, updateListItem, removeListItem,
    addFaq, updateFaq, removeFaq
  };
};
